import Comment from './Comment.js';
import Poll from './Poll.js';
import { w3cwebsocket } from 'websocket';

export const WsClientEvents = {
  ERROR: 'error',
  WS_CONNECT: 'wsConnect',
  WS_CLOSE: 'wsClose',
  WS_MESSAGE: 'wsMessage',
  JOINED: 'joined',
  INFO: 'info',
  STREAM_STATUS_CHANGED: 'streamStatusChanged',
  CHAT_BANNED: 'chatBanned',
  CHAT_UNBANNED: 'chatUnbanned',
  CHAT_MESSAGE: 'chatMessage',
  CHAT_MESSAGES: 'chatMessages',
  CHAT_MESSAGE_DELETED: 'chatMessageDeleted',
  CHAT_MESSAGE_DELETE_FEEDBACK: 'chatMessageDeleteFeedback',
  CHAT_FEEDBACK: 'chatFeedback',
  CHAT_MESSAGE_RECOVERED: 'chatMessageRecovered',
  CHAT_MESSAGE_RECOVER_FEEDBACK: 'chatMessageRecoverFeedback',
  CHAT_MESSAGE_PINNED: 'chatMessagePinned',
  CHAT_MESSAGE_PIN_FEEDBACK: 'chatMessagePinFeedback',
  CHAT_MESSAGE_UNPINNED: 'chatMessageUnpinned',
  CHAT_MESSAGE_UNPIN_FEEDBACK: 'chatMessageUnpinFeedback',
  POLLS: 'polls',
  POLL_VOTE: 'pollVote',
  POLL_VOTE_FEEDBACK: 'pollVoteFeedback',
  POLL_RESULTS: 'pollResults',
  MATCH_INFO: 'matchInfo',
  CHEER: 'cheer',
  CHEER_RESULTS: 'cheerResults',
  HEART: 'heart',
  FEED_INFO: 'feedInfo',
}

export class WsClient {

  constructor({ wsUrl, apiKey, isAdmin = false, clientPrivateKey = null, serverPublicKey = null }) {
    if (clientPrivateKey && serverPublicKey) {
      this._loadKeys(clientPrivateKey, serverPublicKey);
    }

    this._clientId = this._generateClientId();

    this._wsUrl = wsUrl;
    this._apiKey = apiKey;
    this._isAdmin = isAdmin;

    this._closedManually = false;
    this._isConnected = false
    this._isJoined = false;
    this._isPlaying = false;
    this._isBanned = false;
    this._userId = 0;
    this._chatMessagesBuffer = [];
    this._eventListeners = {};

    this._sendPlayTimer = null;

    this._chatName = '';

    this._connect();
  }

  async _loadKeys(decryptKey, encryptKey) {
    let promises = [];
    promises.push(
      crypto.subtle.importKey(
        'pkcs8',
        Buffer.from(decryptKey, 'base64'),
        { name: 'RSA-OAEP', hash: { name: 'SHA-256' } },
        true,
        ['decrypt']
      )
    );
    promises.push(
      crypto.subtle.importKey(
        'spki',
        Buffer.from(encryptKey, 'base64'),
        { name: 'RSA-OAEP', hash: { name: 'SHA-256' } },
        true,
        ['encrypt']
      )
    );

    Promise.all(promises)
      .then(keys => {
        this._decryptKey = keys[0];
        this._encryptKey = keys[1];
      })
      .catch(err => {
        this._decryptKey = null;
        this._encryptKey = null;
        this._log('Encrypt or decrypt key import error');
        console.error(err);
      });
  }

  async _encryptMessage(message) {
    try {
      const iv = crypto.getRandomValues(new Uint8Array(16));
      const encrypted = await crypto.subtle.encrypt(
        { name: 'RSA-OAEP', iv },
        this._encryptKey,
        Buffer.from(message),
      );

      return Buffer.from(encrypted).toString('base64') + '.' + Buffer.from(iv).toString('base64');
    }
    catch (err) {
      this._log('Message encryption error.');
      console.error(err);
    }

    return message;
  }

  async _decryptMessage(message) {
    if (typeof message !== 'string' || message.length === 0 || message[0] === '{') {
      return message;
    }

    let result = '';

    try {
      const chunks = message.split('-');
      for (let chunk of chunks) {
        const parts = chunk.split('.', 2);
        const encryptedMessage = Buffer.from(parts[0], 'base64');
        const iv = Buffer.from(parts[1], 'base64');

        const content = await crypto.subtle.decrypt(
          { name: 'RSA-OAEP', iv },
          this._decryptKey,
          encryptedMessage
        );
        result += (new TextDecoder()).decode(content);
      }
    }
    catch (err) {
      this._log('Message decryption error.');
      console.error(err);
      result = '{}';
    }

    return result;
  }

  _connect() {
    let url = this._wsUrl;
    if (this._apiKey) {
      url += '/' + this._apiKey + '?client_id=' + this._clientId;
      if (this._isAdmin) {
        url += '&admin';
      }
    }

    this._client = new w3cwebsocket(url);
    this._client.onerror = (err) => this._onError(err);
    this._client.onopen = () => this._onConnect();
    this._client.onclose = (event) => this._onClose(event);
    this._client.onmessage = (message) => this._onMessage(message);
  }

  addEventListener(event, callback, listenerId) {
    if (!this._eventListeners[event]) {
      this._eventListeners[event] = [];
    }

    this._eventListeners[event].push({ callback, listenerId });

    if (event === WsClientEvents.CHAT_MESSAGES && this._chatMessagesBuffer.length !== 0) {
      this._chatMessagesBuffer.forEach(item => {
        this._fireEvent(event, item);
      });
      this._chatMessagesBuffer = [];
    }
  }

  removeEventListener(event, callback) {
    if (!this._eventListeners[event]) {
      return;
    }

    this._eventListeners[event] = this._eventListeners[event].filter(item => item.callback != callback);
  }

  get isConnected() {
    return this._isConnected;
  }

  get isJoined() {
    return this._isJoined;
  }

  get isBanned() {
    return this._isBanned;
  }

  get userId() {
    return this._userId;
  }

  set chatName(val) {
    this._chatName = val.toString().trim();
  }

  _onConnect(connection) {
    this._isConnected = true;
    this._log('Conncetion is live');
    this._fireEvent(WsClientEvents.WS_CONNECT, connection);

    if (this._isPlaying) {
      this.play();
    }

    setTimeout(() => {
      if (!this._isJoined) {
        this.disconnect();
        this._connect();
      }
    }, 2000);
  }

  _onError(error) {
    this._log('WS connection error');
    console.error(error);
    this._fireEvent(WsClientEvents.ERROR, error);
  }

  _onClose(event) {
    console.log('WS Close code: ' + event.code);

    this._isConnected = false;
    this._isJoined = false;
    this._log('Connection Closed');
    this._fireEvent(WsClientEvents.WS_CLOSE);

    if (!this._closedManually) {
      setTimeout(() => this._connect(), 500);
    }
  }

  async _onMessage(message) {
    try {
      let messageContent = message.data;
      if (this._encryptKey && this._decryptKey) {
        messageContent = await this._decryptMessage(message.data);
      }

      this._fireEvent(WsClientEvents.WS_MESSAGE, messageContent);

      const parsedMessage = JSON.parse(messageContent);
      this._processMessage(parsedMessage);
    }
    catch (err) {
      console.error(err);
    }
  }

  getInfo() {
    const msg = {
      command: 'getinfo',
    };

    this._submit(msg);
  }

  play() {
    this._isPlaying = true;

    if (this._sendPlayTimer !== null) {
      clearTimeout(this._sendPlayTimer);
      this._sendPlayTimer = null;
    }

    this._sendPlayTimer = setTimeout(() => {
      this._sendPlayTimer = null;
      this._submit({ command: 'play' })
    }, 250);
  }

  pause() {
    this._isPlaying = false;

    if (this._sendPlayTimer !== null) {
      clearTimeout(this._sendPlayTimer);
      this._sendPlayTimer = null;
    }
    else {
      this._submit({ command: 'pause' });
    }
  }

  chatMessage(message, sendTo) {
    if (this._isBanned) {
      throw new Error('You are currently banned, so you cannot post messages.');
    }

    const clientGeneratedId = Array(32).fill().map(() => 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.charAt(Math.random()*62)).join('');

    const msg = {
      command: 'chat',
      name: this._chatName,
      message,
      clientGeneratedId,
      sendTo,
    };

    this._submit(msg);

    const comment = new Comment({
      clientGeneratedId,
      message,
      isLoading: true,
    });

    return comment;
  }

  chatGetMessages({ before = new Date(), limit = 20 }) {
    const msg = {
      command: 'getChatMessages',
      before: before.getTime(),
      limit,
      isAdmin: this._isAdmin,
    };

    this._submit(msg);
  }

  chatBan({ userId }) {
    const msg = {
      command: 'ban',
      bannedUserId: userId,
    };

    this._submit(msg);
  }

  chatUnban({ userId }) {
    const msg = {
      command: 'unban',
      bannedUserId: userId,
    };

    this._submit(msg);
  }

  chatMessageDelete(messageId) {
    const msg = {
      command: 'messageDelete',
      messageId,
    };

    this._submit(msg);
  }

  chatMessageRecover(messageId) {
    const msg = {
      command: 'messageRecover',
      messageId,
    };

    this._submit(msg);
  }

  chatMessagePin(messageId) {
    const msg = {
      command: 'messagePin',
      messageId,
    };

    this._submit(msg);
  }

  chatMessageUnpin() {
    const msg = {
      command: 'messageUnpin',
    };

    this._submit(msg);
  }

  feedInfo(content) {
    const msg = {
      command: 'feedinfo',
      feedInfo: content,
    };

    this._submit(msg);
  }

  heart() {
    const msg = {
      command: 'heart',
    };

    this._submit(msg);
  }

  pollVote(pollId, answerId) {
    const msg = {
      command: 'pollVote',
      pollId,
      answerId,
    };

    this._submit(msg);
  }

  pollGetResults(pollId) {
    const msg = {
      command: 'pollGetResults',
      pollId,
    };

    this._submit(msg);
  }

  cheer(index) {
    const msg = {
      command: 'cheer',
      index,
    };

    this._submit(msg);
  }

  async _submit(message) {
    message['apiKey'] = this._apiKey;
    message['userId'] = this._userId;
    message['ts'] = Date.now();

    if (this._client.readyState === this._client.OPEN) {
      message = JSON.stringify(message);
      if (this._encryptKey && this._decryptKey) {
        message = await this._encryptMessage(message);
      }

      this._client.send(message);
    }
  }

  _processMessage(message) {
    switch (message.error) {
      case 'hasNoFreeViewerCapacity':
        return this._processErrorHasNoFreeViewerCapacity(message);
    }

    switch (message.command) {
      case 'joined':
        return this._processMessageJoined(message);

      case 'info':
        return this._processMessageInfo(message);

      case 'chat':
        return this._processMessageChat(message);

      case 'chatMessages':
        return this._processMessageChatMessages(message);

      case 'banned':
        return this._processMessageBanned(message);

      case 'unbanned':
        return this._processMessageUnbanned(message);

      case 'messageDeleted':
        return this._processMessageDeleted(message);

      case 'messageRecovered':
        return this._processMessageRecovered(message);

      case 'messagePinned':
        return this._processMessagePinned(message);

      case 'messageUnpinned':
        return this._processMessageUnpinned(message);

      case 'heart':
        return this._processMessageHeart(message);

      case 'feedinfo':
        return this._processMessageFeedInfo(message);

      case 'streamStatusChange':
        return this._processMessageStreamStatusChange(message);

      case 'chatFeedback':
        return this._processMessageChatFeedback(message);

      case 'chatMessageDeleteFeedback':
        return this._processMessageChatMessageDeleteFeedback(message);

      case 'chatMessageRecoverFeedback':
        return this._processMessageChatMessageRecoverFeedback(message);

      case 'chatMessagePinFeedback':
        return this._processMessageChatMessagePinFeedback(message);

      case 'chatMessageUnpinFeedback':
        return this._processMessageChatMessageUnpinFeedback(message);

      case 'polls':
        return this._processMessagePolls(message);

      case 'pollVoteFeedback':
        return this._processMessagePollVoteFeedback(message);

      case 'pollVote':
        return this._processMessagePollVote(message);

      case 'pollResults':
        return this._processMessagePollResults(message);

      case 'matchinfo':
        return this._processMessageMatchInfo(message);

      case 'cheer':
        return this._processMessageCheer(message);

      case 'cheerResults':
        return this._processMessageCheerResults(message);

      default:
        this._log('An unreadable message was received from the server.', 'warn');
        console.warn(message);
    }
  }

  _processMessageJoined(message) {
    this._log('Joined to chat room (' + this._apiKey + ')');

    this._userId = message.userId;
    this._isJoined = true;
    this._fireEvent(WsClientEvents.JOINED, message);

    if (
      'feedInfo' in message
      && Array.isArray(message.feedInfo)
      && message.feedInfo.length !== 0
    ) {
      this._processMessageFeedInfo(message);
    }
  }

  _processMessageInfo(message) {
    this._fireEvent(WsClientEvents.INFO, message);
  }

  _processMessageChat(message) {
    const comment = new Comment({
      id: message.data.id,
      message: message.data.message,
      image: message.data.image,
      isDeleted: message.data.isDeleted,
      platforms: message.data.platforms,
      createdAt: new Date(message.data.createdAt),
    });

    this._fireEvent(WsClientEvents.CHAT_MESSAGE, comment);
  }

  _processMessageChatMessages(message) {
    let comments = [];
    message.data.forEach(item => {
      const comment = new Comment({
        id: item.id,
        message: item.message,
        image: item.image,
        isDeleted: item.isDeleted,
        platforms: item.platforms,
        createdAt: new Date(item.createdAt),
      });
      comments.push(comment);
    });

    if (!this._hasListener(WsClientEvents.CHAT_MESSAGES)) {
      this._chatMessagesBuffer.push({ comments, pinned: message.pinned });
      return;
    }

    this._fireEvent(WsClientEvents.CHAT_MESSAGES, { comments, pinned: message.pinned });
  }

  _processMessageBanned(message) {
    this._isBanned = true;
    this._fireEvent(WsClientEvents.CHAT_BANNED, message);
  }

  _processMessageUnbanned(message) {
    this._isBanned = false;
    this._fireEvent(WsClientEvents.CHAT_UNBANNED, message);
  }

  _processMessageDeleted(message) {
    this._fireEvent(WsClientEvents.CHAT_MESSAGE_DELETED, message);
  }

  _processMessageRecovered(message) {
    this._fireEvent(WsClientEvents.CHAT_MESSAGE_RECOVERED, message);
  }

  _processMessagePinned(message) {
    const comment = new Comment(message.data);
    this._fireEvent(WsClientEvents.CHAT_MESSAGE_PINNED, comment);
  }

  _processMessageUnpinned(message) {
    this._fireEvent(WsClientEvents.CHAT_MESSAGE_UNPINNED, message);
  }

  _processMessageHeart(message) {
    this._fireEvent(WsClientEvents.HEART, message);
  }

  _processMessageFeedInfo(message) {
    this._fireEvent(WsClientEvents.FEED_INFO, message.feedInfo);
  }

  _processMessageStreamStatusChange(message) {
    this._fireEvent(WsClientEvents.STREAM_STATUS_CHANGED, message.status);
  }

  _processMessageChatFeedback(message) {
    const success = message.success;
    let comment = null;

    if (success) {
      comment = new Comment(message.data);
      if (comment.smucPlatform) {
        comment.smucPlatform.name = 'You';
      }
    }

    this._fireEvent(WsClientEvents.CHAT_FEEDBACK, { success, comment });
  }

  _processMessageChatMessageDeleteFeedback(message) {
    this._fireEvent(WsClientEvents.CHAT_MESSAGE_DELETE_FEEDBACK, message);
  }

  _processMessageChatMessageRecoverFeedback(message) {
    this._fireEvent(WsClientEvents.CHAT_MESSAGE_RECOVER_FEEDBACK, message);
  }

  _processMessageChatMessagePinFeedback(message) {
    this._fireEvent(WsClientEvents.CHAT_MESSAGE_PIN_FEEDBACK, message);
  }

  _processMessageChatMessageUnpinFeedback(message) {
    this._fireEvent(WsClientEvents.CHAT_MESSAGE_UNPIN_FEEDBACK, message);
  }

  _processMessagePolls(message) {
    const polls = message.data.map(poll => new Poll(poll));
    this._fireEvent(WsClientEvents.POLLS, polls);
  }

  _processMessagePollVote(message) {
    this._fireEvent(WsClientEvents.POLL_VOTE, message);
  }

  _processMessagePollVoteFeedback(message) {
    this._fireEvent(WsClientEvents.POLL_VOTE_FEEDBACK, message);
  }

  _processMessagePollResults(message) {
    this._fireEvent(WsClientEvents.POLL_RESULTS, message);
  }

  _processMessageMatchInfo(message) {
    this._fireEvent(WsClientEvents.MATCH_INFO, message.data);
  }

  _processMessageCheer(message) {
    this._fireEvent(WsClientEvents.CHEER, message);
  }

  _processMessageCheerResults(message) {
    this._fireEvent(WsClientEvents.CHEER_RESULTS, message);
  }

  _processErrorHasNoFreeViewerCapacity(message) {
    const error = new HasNoFreeViewerCapacityError();
    this._fireEvent(WsClientEvents.ERROR, error);
    this.disconnect();
  }

  disconnect() {
    this._closedManually = true;
    this._client.close();
  }

  _fireEvent(event, payload) {
    if (!this._hasListener(event)) {
      return;
    }

    this._eventListeners[event].forEach(item => item.callback(payload, item.listenerId));
  }

  _hasListener(event) {
    return event in this._eventListeners;
  }

  _log(content, type) {
    if (!type) {
      type = 'info';
    }

    console[type]('[WS Client] ' + content);
  }

  _generateClientId() {
    if (window && window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }

    return (Math.random() + 1).toString(36).substring(2, 8) +
      '-' + (Math.random() + 1).toString(36).substring(2, 4) +
      '-' + (Math.random() + 1).toString(36).substring(2, 4) +
      '-' + (Math.random() + 1).toString(36).substring(2, 4) +
      '-' + (Math.random() + 1).toString(36).substring(2, 6) + (Math.random() + 1).toString(36).substring(2, 6);
  }

}

export class HasNoFreeViewerCapacityError extends Error {
  constructor() {
    super();
    this.message = 'You cannot join at this time because the broadcast has reached the maximum number of viewers.';
  }
}
